/**
 * 更新检查策略（与游戏厅 tauri/src/stores/update.ts 对齐）
 * 互斥：weekly @ 星期+HH:mm | startup（启动约 5s 后检查）| manual（仅手动）
 */
import type { UpdateCheckMode } from '@/utils/cardshopOps'

const DEFAULT_WEEKLY_AT = '03:00'
const DEFAULT_WEEKLY_DOW = 1

let bootTimer: ReturnType<typeof setTimeout> | null = null
let weeklyTimer: ReturnType<typeof setTimeout> | null = null
let resumeBound = false
let activeRunCheck: (() => Promise<void>) | null = null
let lastWeeklyFireYmd = ''

let mode: UpdateCheckMode = 'startup'
let weeklyDow = DEFAULT_WEEKLY_DOW
let weeklyAt = DEFAULT_WEEKLY_AT

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function todayYmd(d = new Date()): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function normalizeDailyAt(raw: unknown): string {
  const s = String(raw ?? '').trim()
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s)
  if (!m) return DEFAULT_WEEKLY_AT
  const h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
    return DEFAULT_WEEKLY_AT
  }
  return `${pad2(h)}:${pad2(min)}`
}

export function normalizeWeeklyDow(raw: unknown): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return DEFAULT_WEEKLY_DOW
  const d = Math.floor(n)
  if (d < 0 || d > 6) return DEFAULT_WEEKLY_DOW
  return d
}

export function normalizeUpdateCheckMode(raw: unknown): UpdateCheckMode {
  const s = String(raw ?? 'startup').toLowerCase()
  if (s === 'manual' || s === 'none' || s === 'off') return 'manual'
  if (s === 'startup' || s === 'boot') return 'startup'
  if (s === 'interval') return 'startup'
  if (s === 'weekly' || s === 'daily') return 'weekly'
  return 'startup'
}

function parseWeeklyAt(hhmm: string): { h: number; m: number } {
  const n = normalizeDailyAt(hhmm)
  const [hs, ms] = n.split(':')
  return { h: Number(hs), m: Number(ms) }
}

export function msUntilNextWeekly(dow: number, hhmm: string, now = new Date()): number {
  const { h, m } = parseWeeklyAt(hhmm)
  const next = new Date(now)
  next.setSeconds(0, 0)
  next.setHours(h, m, 0, 0)
  let diff = dow - now.getDay()
  if (diff < 0 || (diff === 0 && next.getTime() <= now.getTime())) {
    diff += 7
  }
  next.setDate(now.getDate() + diff)
  return Math.max(1_000, next.getTime() - now.getTime())
}

function thisWeekTargetYmd(dow: number, hhmm: string, now = new Date()): string {
  const { h, m } = parseWeeklyAt(hhmm)
  const target = new Date(now)
  target.setSeconds(0, 0)
  target.setHours(h, m, 0, 0)
  const diff = dow - now.getDay()
  target.setDate(now.getDate() + diff)
  return todayYmd(target)
}

function thisWeekTargetMs(dow: number, hhmm: string, now = new Date()): number {
  const { h, m } = parseWeeklyAt(hhmm)
  const target = new Date(now)
  target.setSeconds(0, 0)
  target.setHours(h, m, 0, 0)
  const diff = dow - now.getDay()
  target.setDate(now.getDate() + diff)
  return target.getTime()
}

function clearWeeklyTimer() {
  if (weeklyTimer != null) {
    clearTimeout(weeklyTimer)
    weeklyTimer = null
  }
}

function onResume() {
  if (document.visibilityState === 'hidden') return
  if (!activeRunCheck) return
  rescheduleAfterWake(activeRunCheck)
}

export function setUpdatePolicy(opts: {
  mode?: unknown
  weeklyDow?: unknown
  weeklyAt?: unknown
  dailyAt?: unknown
}) {
  mode = normalizeUpdateCheckMode(opts.mode ?? mode)
  if (opts.weeklyDow != null) weeklyDow = normalizeWeeklyDow(opts.weeklyDow)
  const at = opts.weeklyAt ?? opts.dailyAt
  if (at != null) weeklyAt = normalizeDailyAt(at)
}

export function getUpdatePolicy() {
  return { mode, weeklyDow, weeklyAt }
}

export function clearUpdateTimers() {
  if (bootTimer != null) {
    clearTimeout(bootTimer)
    bootTimer = null
  }
  clearWeeklyTimer()
}

function bindResumeListeners() {
  if (resumeBound || typeof window === 'undefined') return
  resumeBound = true
  document.addEventListener('visibilitychange', onResume)
  window.addEventListener('online', onResume)
  window.addEventListener('focus', onResume)
}

export function unbindUpdateResumeListeners() {
  if (!resumeBound || typeof window === 'undefined') return
  resumeBound = false
  document.removeEventListener('visibilitychange', onResume)
  window.removeEventListener('online', onResume)
  window.removeEventListener('focus', onResume)
}

function scheduleWeekly(runCheck: () => Promise<void>) {
  clearWeeklyTimer()
  const ms = msUntilNextWeekly(weeklyDow, weeklyAt)
  weeklyTimer = setTimeout(() => {
    lastWeeklyFireYmd = thisWeekTargetYmd(weeklyDow, weeklyAt)
    void runCheck().finally(() => scheduleWeekly(runCheck))
  }, ms)
}

export function rescheduleAfterWake(runCheck: () => Promise<void>) {
  activeRunCheck = runCheck
  if (mode === 'startup' || mode === 'manual') return

  clearWeeklyTimer()
  const now = new Date()
  const ymd = thisWeekTargetYmd(weeklyDow, weeklyAt, now)
  const target = thisWeekTargetMs(weeklyDow, weeklyAt, now)
  if (now.getTime() >= target && lastWeeklyFireYmd !== ymd) {
    lastWeeklyFireYmd = ymd
    void runCheck().finally(() => scheduleWeekly(runCheck))
    return
  }
  scheduleWeekly(runCheck)
}

export function startUpdatePolicy(runCheck: () => Promise<void>) {
  clearUpdateTimers()
  activeRunCheck = runCheck

  if (mode === 'manual') {
    unbindUpdateResumeListeners()
    return
  }

  bindResumeListeners()

  if (mode === 'startup') {
    bootTimer = setTimeout(() => {
      void runCheck()
    }, 5_000)
    return
  }

  scheduleWeekly(runCheck)
}

export function refreshUpdateTimers(runCheck: () => Promise<void>) {
  activeRunCheck = runCheck
  clearUpdateTimers()
  if (mode === 'startup' || mode === 'manual') {
    if (mode === 'manual') unbindUpdateResumeListeners()
    return
  }
  bindResumeListeners()
  scheduleWeekly(runCheck)
}

/** 星期几文案（0=周日） */
export const WEEKDAY_LABELS_ZH = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export function formatWeeklyScheduleLabel(dow: number, at: string): string {
  const d = normalizeWeeklyDow(dow)
  const day = WEEKDAY_LABELS_ZH[d] ?? `周${d}`
  return `${day} ${normalizeDailyAt(at)}`
}
